import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { fmtDayTime } from "../home/time";
import Icon from "./Icon";
import { errorMessage, submitFinal } from "./api";

const METHODS = [["file", "Upload a File", "upload"], ["text", "Write Text", "task"], ["link", "Add a Link", "link"]];
const KIND = { file: "File Upload", text: "Text", link: "Link" };
const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/**
 * Final submission (prototype v8): file, text or link → review step → submit. Once sent it can't be
 * changed or withdrawn; the API refuses a second submission.
 */
export default function SubmissionForm({ kind, id, forLabel, forValue, withTitle = false, team = null, onSubmitted }) {
  const startedAt = useRef(Date.now());
  const [method, setMethod] = useState("file");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [title, setTitle] = useState("");
  const [step, setStep] = useState("edit");
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);
  // Team projects: the rep ticks who took part (everyone to start). Only they share the grade; the rep always counts.
  const [took, setTook] = useState(() => new Set((team?.people || []).map((p) => p.id)));
  const toggle = (pid) => setTook((s) => { const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });
  const people = team?.people || [];
  const tookNames = people.filter((p) => p.id === team?.rep_id || took.has(p.id)).map((p) => p.name);
  const leftNames = people.filter((p) => p.id !== team?.rep_id && !took.has(p.id)).map((p) => p.name);
  const noun = kind === "project" ? "project" : "assignment";

  const review = () => {
    const problem = withTitle && !title.trim() ? "Add a project title first."
      : method === "file" && !file ? "Choose a file to upload first."
      : method === "file" && file.size > 10 * 1048576 ? "Files can be up to 10 MB."
      : method === "text" && !text.trim() ? "Write your answer first."
      : method === "link" && !/^https?:\/\/\S+\.\S+/.test(link.trim()) ? "Add a full link that starts with https://"
      : "";
    setErr(problem);
    if (!problem) { setConfirmed(false); setStep("review"); }
  };

  const send = async () => {
    setSending(true);
    try {
      await submitFinal(kind, id, {
        method,
        title: withTitle ? title.trim() : undefined,
        file: method === "file" ? file : undefined,
        answer_text: method === "text" ? text : undefined,
        file_link: method === "link" ? link.trim() : undefined,
        participants: team ? people.filter((p) => p.id === team.rep_id || took.has(p.id)).map((p) => p.id) : undefined,
      }, startedAt.current);
      toast.success(`${noun === "project" ? "Project" : "Assignment"} submitted`);
      onSubmitted?.();
    } catch (e) {
      toast.error(errorMessage(e));
      setStep("edit");
    }
    setSending(false);
  };

  if (step === "review") {
    return (
      <section className="card review-sub">
        <div className="card-h"><h3>Review Your Submission</h3><span className="st due">Not Sent Yet</span></div>
        <p>Check that this is exactly what you want to submit. <strong>Once you submit, it's final: you can't edit, replace or withdraw it.</strong></p>
        <dl className="sub-sum">
          {withTitle && <div><dt>Project title</dt><dd>{title}</dd></div>}
          <div><dt>{forLabel}</dt><dd>{forValue}</dd></div>
          <div><dt>Submitted as</dt><dd>{KIND[method]}</dd></div>
          {team && <div><dt>Took part</dt><dd>{tookNames.join(", ")}</dd></div>}
          {team && leftNames.length > 0 && <div><dt>Not included</dt><dd>{leftNames.join(", ")} · won't share the team's grade</dd></div>}
        </dl>
        <SubPreview method={method} name={file && `${file.name} · ${fmtSize(file.size)}`} link={link} text={text} />
        <label className="confirm">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          <span>{kind === "project"
            ? "I'm submitting this for my whole team. I understand it's final and can't be changed or withdrawn after I submit."
            : "I understand this is my final submission. I can't change or withdraw it after I submit."}</span>
        </label>
        <div className="cta-row">
          <button className="cbtn quiet" type="button" onClick={() => setStep("edit")}>Go Back and Edit</button>
          <button className="cbtn go" type="button" onClick={send} disabled={!confirmed || sending}>{sending ? "Submitting…" : `Submit final ${noun}`}</button>
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <div className="card-h"><h3>{kind === "project" ? "Submit for Your Team" : "Your Submission"}</h3><span className="st due">Not Submitted</span></div>
      {withTitle && (
        <div className="form"><label htmlFor={`t-${id}`}>Project title<input id={`t-${id}`} value={title} onChange={(e) => setTitle(e.target.value)} /></label></div>
      )}
      <div className="sub-method">
        <span className="sub-label">How do you want to submit?</span>
        <div className="lb-tabs" role="group" aria-label="Submission Type">
          {METHODS.map(([k, l, icon]) => (
            <button key={k} type="button" aria-pressed={method === k} onClick={() => { setMethod(k); setErr(""); }}><Icon name={icon} size={15} /> {l}</button>
          ))}
        </div>
      </div>
      {method === "file" && (
        <label className="drop" htmlFor={`f-${id}`}>
          <Icon name="upload" size={28} />
          <strong>{file ? file.name : `Drag your ${kind === "project" ? "project file" : "file"} here, or choose a file`}</strong>
          <span>{file ? `${fmtSize(file.size)} · ready to review` : "Up to 10 MB"}</span>
          <span className={`cbtn ${file ? "quiet" : "go"}`}>{file ? "Replace File" : "Choose File"}</span>
          <input id={`f-${id}`} type="file" hidden onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(""); }} />
        </label>
      )}
      {method === "text" && (
        <div className="form"><label htmlFor={`x-${id}`}>Your answer
          <textarea id={`x-${id}`} rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write or paste your answer here" /></label></div>
      )}
      {method === "link" && (
        <div className="form"><label htmlFor={`l-${id}`}>Link to your work
          <input id={`l-${id}`} type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://github.com/… or a Google Drive or Colab link" />
          <small>Make sure anyone with the link can view it.</small></label></div>
      )}
      {team && people.length > 1 && (
        <fieldset className="took-part">
          <legend>Who took part?</legend>
          <small>Untick anyone who didn't contribute. Only the people ticked share the team's grade.</small>
          {people.map((p) => (
            <label key={p.id}>
              <input type="checkbox" checked={p.id === team.rep_id || took.has(p.id)} disabled={p.id === team.rep_id} onChange={() => toggle(p.id)} />
              <span>{p.name}{p.id === team.rep_id ? " · you, the rep" : ""}</span>
            </label>
          ))}
        </fieldset>
      )}
      {err && <p className="sub-err">{err}</p>}
      <div className="cta-row">
        <button className="cbtn go" type="button" onClick={review}>Review Submission</button>
        <small>Nothing is sent until you confirm on the next step.</small>
      </div>
    </section>
  );
}

function SubPreview({ method, name, link, text, url }) {
  if (method === "text") return <blockquote className="sub-text">{text}</blockquote>;
  return (
    <div className="file">
      <span className="ico doc"><Icon name={method === "link" ? "link" : "files"} /></span>
      <div><strong className="any">{url ? <a href={url} target="_blank" rel="noopener noreferrer">{method === "link" ? link : name}</a> : method === "link" ? link : name}</strong><small>{KIND[method]}</small></div>
      <span />
    </div>
  );
}

/** What was sent (prototype sentBox + sentCard), then grade and feedback once marked. */
export function SubmittedCard({ submission, title, note }) {
  const method = submission.file_name ? "file" : submission.file_link ? "link" : "text";
  const sent = submission.submitted_at ? fmtDayTime(submission.submitted_at) : "";
  return (
    <>
      <section className="complete-box ok">
        <div>
          <strong>✓ Submitted {sent} · {submission.grade ? `graded ${submission.grade}` : "waiting to be graded"}</strong>
          <small>{note || "You'll get a notification when it's graded."}</small>
        </div>
      </section>
      <section className="card">
        <div className="card-h"><h3>{title || "What Was Submitted"}</h3><span className="course-tag">Final · can't be changed</span></div>
        <SubPreview method={method} name={submission.file_name} link={submission.file_link} text={submission.answer_text} url={submission.file_url || submission.file_link} />
        {method !== "text" && submission.answer_text && <blockquote className="sub-text">{submission.answer_text}</blockquote>}
      </section>
      {submission.feedback && (
        <section className="card"><div className="card-h"><h3>Instructor Feedback</h3></div><p className="feedback">{submission.feedback}</p></section>
      )}
    </>
  );
}
